import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { resolveBranchScopeLogoUrl, resolveSelectedLegalEntityLogoUrl } from "@/lib/agencyLogo";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, Settings, Building2, LogOut, User, ChevronDown, LifeBuoy, UserCog, ShieldCheck, Sun, Moon } from "lucide-react";
import { Avatar } from "@/components/atoms/Avatar";
import { SearchInput } from "@/components/molecules/SearchInput";
import { useSettingsStore } from "@/store/settingsStore";
import { useProfileStore } from "@/store/profileStore";
import { selectEffectiveTheme, useThemeStore } from "@/store/themeStore";
import { useKeycloak } from "@/modules/auth/context/KeycloakContext";
import { usePermissions } from "@/context/PermissionsProvider";
import { LegalEntityHierarchy } from "@/services/profileService";
import { cn } from "@/lib/cn";
import { Tooltip } from "@/components/atoms/Tooltip";
import {
  navTooltipContent,
  THEME_TOGGLE_SHORTCUT_KEY,
  type AppNavShortcutKey,
} from "@/lib/appNavShortcuts";
import { formatCtrlAltShortcut } from "@/lib/keyboardShortcut";

/** Wrap menu rows so Tooltip never merges `block` onto a flex trigger (breaks alignment). */
function UserMenuShortcutTooltip({
  label,
  shortcutKey,
  children,
}: {
  label: string;
  shortcutKey: AppNavShortcutKey;
  children: ReactElement;
}) {
  return (
    <Tooltip
      content={navTooltipContent(label, shortcutKey)}
      side="left"
      hoverOnly
      className="block w-full"
    >
      <div className="w-full">{children}</div>
    </Tooltip>
  );
}

/**
 * Light / dark switch — the knob carries the icon of the active theme. Disabled
 * (showing light) while a page has locked the theme, e.g. Conversations.
 */
function ThemeSwitch() {
  const isDark = useThemeStore((s) => selectEffectiveTheme(s) === "dark");
  const locked = useThemeStore((s) => s.lightLocked);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  const label = locked
    ? "Dark mode isn't available in Conversations"
    : `${isDark ? "Switch to light mode" : "Switch to dark mode"}\n${formatCtrlAltShortcut(THEME_TOGGLE_SHORTCUT_KEY)}`;

  return (
    <Tooltip content={label} side="bottom" hoverOnly>
      {/* A disabled button never fires mouse events, so the tooltip hangs off this wrapper. */}
      <span className={cn("inline-flex", locked && "cursor-not-allowed")}>
        <button
          type="button"
          role="switch"
          aria-checked={isDark}
          aria-label="Dark mode"
          disabled={locked}
          onClick={toggleTheme}
          className={cn(
            "relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border transition-colors duration-200 focus-ring",
            locked && "pointer-events-none opacity-50",
            isDark
              ? "border-bridge-accent/40 bg-bridge-accent/20"
              : "border-bridge-border bg-slate-100",
          )}
        >
          <span
            className={cn(
              "absolute left-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full shadow-sm transition-transform duration-200 ease-in-out",
              isDark ? "translate-x-5 bg-bridge-accent text-white" : "bg-white text-amber-500",
            )}
          >
            {isDark ? <Moon size={11} strokeWidth={2.5} /> : <Sun size={11} strokeWidth={2.5} />}
          </span>
        </button>
      </span>
    </Tooltip>
  );
}

interface TokenProfile {
  name?: string;
  preferred_username?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  email_verified?: boolean;
}

export function Topbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { legalEntityId, branchId, legalEntityName, branchName, setLegalEntity, setBranch } = useSettingsStore();
  const { profileData, fetchProfile } = useProfileStore();
  const { keycloak, logout } = useKeycloak();
  const { me } = usePermissions();
  const [menuOpen, setMenuOpen] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  // Stays mounted briefly after closing so the exit animation can play.
  const [menuMounted, setMenuMounted] = useState(false);
  useEffect(() => {
    if (menuOpen) {
      setMenuMounted(true);
      return;
    }
    const t = setTimeout(() => setMenuMounted(false), 260);
    return () => clearTimeout(t);
  }, [menuOpen]);
  const [orgSearch, setOrgSearch] = useState("");
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const focusInMenuRef = useRef(false);
  // Runs before paint, ahead of any inert-induced blur: hand focus back to the trigger.
  useLayoutEffect(() => {
    if (menuOpen || !focusInMenuRef.current) return;
    focusInMenuRef.current = false;
    menuTriggerRef.current?.focus();
  }, [menuOpen]);

  const menuRef = useRef<HTMLDivElement>(null);
  const menuCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const orgRef = useRef<HTMLDivElement>(null);

  const profile = (keycloak?.tokenParsed ?? {}) as TokenProfile;
  const email = profile.email;
  const displayName =
    profile.name ||
    [profile.given_name, profile.family_name].filter(Boolean).join(" ") ||
    profile.preferred_username ||
    profile.email ||
    "Bridge Operator";
  const displayEmail = profile.email;
  const profileBelongsToCurrentUser = Boolean(
    email &&
    profileData?.email &&
    profileData.email.trim().toLowerCase() === email.trim().toLowerCase(),
  );
  const showProfileCompletionBanner =
    profileBelongsToCurrentUser && profileData?.profileCompleted === false;
  const topbarAgencyName = useMemo(
    () =>
      profileData?.companyName?.trim() ||
      legalEntityName?.trim() ||
      profileData?.groupName?.trim() ||
      "Agency",
    [profileData, legalEntityName],
  );

  const topbarLogoUrl = useMemo(() => {
    if (!profileData?.legalEntities?.length) return null;

    const isMasterUser = profileData.userType === "ATLAS_MASTER_USER";
    const allowedBranchIds = me?.allowedBranchIds ?? [];
    const lockedBranchId =
      allowedBranchIds.length === 1 ? allowedBranchIds[0] : (me?.branchId ?? null);
    const effectiveBranchId =
      lockedBranchId && !isMasterUser ? lockedBranchId : branchId;

    if (effectiveBranchId) {
      return resolveBranchScopeLogoUrl(
        profileData.legalEntities,
        legalEntityId,
        effectiveBranchId,
      );
    }

    return resolveSelectedLegalEntityLogoUrl(
      profileData.legalEntities,
      legalEntityId,
    );
  }, [profileData, legalEntityId, branchId, me]);

  useEffect(() => {
    if (!email) return;
    fetchProfile(email);
  }, [email, fetchProfile]);

  const flattenEntities = (entities: LegalEntityHierarchy[], depth = 0): (LegalEntityHierarchy & { depth: number })[] => {
    return entities.reduce((acc, le) => {
      acc.push({ ...le, depth });
      if (le.subEntities && le.subEntities.length > 0) {
        acc.push(...flattenEntities(le.subEntities, depth + 1));
      }
      return acc;
    }, [] as (LegalEntityHierarchy & { depth: number })[]);
  };

  const allLegalEntities = profileData ? flattenEntities(profileData.legalEntities) : [];
  const currentLe = allLegalEntities.find(le => le.id === legalEntityId);

  // Master users pick an Organization (root LE); everything narrower is chosen on the
  // page itself through the ScopePicker. The `/profile` payload returns every root with
  // its nested subEntities, so the selected org is the root whose subtree contains the
  // current legalEntityId — no extra persisted state needed.
  const rootLegalEntities = profileData?.legalEntities ?? [];
  const orgSearchQuery = orgSearch.trim().toLowerCase();
  const filteredOrganizations = orgSearchQuery
    ? rootLegalEntities.filter((org) => org.name.toLowerCase().includes(orgSearchQuery))
    : rootLegalEntities;

  useEffect(() => {
    if (!orgMenuOpen) setOrgSearch("");
  }, [orgMenuOpen]);

  const currentOrg = legalEntityId
    ? rootLegalEntities.find(root => flattenEntities([root]).some(e => e.id === legalEntityId))
    : undefined;

  // Branch scoping: /me reports every branch the user may see. A user holding exactly one
  // branch is pinned to it, and the global context still carries that branch so requests
  // without a scope picker stay narrowed. (Master Users are exempt.)
  const isMasterUser = profileData?.userType === "ATLAS_MASTER_USER";
  const allowedBranchIds = me?.allowedBranchIds ?? [];
  // Fall back to the legacy single-value field for older /me payloads.
  const lockedBranchId = allowedBranchIds.length === 1 ? allowedBranchIds[0] : (me?.branchId ?? null);
  const isBranchScoped = !!lockedBranchId && !isMasterUser;
  // Holding one branch says nothing about how many legal entities the user spans: an
  // organisation with two legal entities and a single branch left the legal-entity
  // selector disabled, stranding the user on the child entity. Lock it only when there
  // is genuinely nothing else to pick.
  const selectableLegalEntityCount = me?.visibleLegalEntityIds?.length ?? allLegalEntities.length;
  const isLegalEntityLocked = isBranchScoped && selectableLegalEntityCount <= 1;
  const lockedLe = isBranchScoped
    ? (allLegalEntities.find(le => le.id === me?.legalEntityId)
      ?? allLegalEntities.find(le => le.branches.some(br => br.id === lockedBranchId)))
    : undefined;
  const lockedBranch = lockedLe?.branches.find(br => br.id === lockedBranchId);

  // Sync displayed branch/LE names with profile data when it changes
  // (e.g. after a CRM integration save updates names server-side).
  useEffect(() => {
    if (!profileData || isBranchScoped) return;
    if (branchId && currentLe) {
      const freshBranch = currentLe.branches.find(br => br.id === branchId);
      if (freshBranch && freshBranch.name !== branchName) {
        setBranch(branchId, freshBranch.name);
      }
    }
    if (legalEntityId) {
      const freshLe = allLegalEntities.find(le => le.id === legalEntityId);
      if (freshLe && freshLe.name !== legalEntityName) {
        setLegalEntity(legalEntityId, freshLe.name);
      }
    }
  }, [profileData]);

  // Auto-select legal entity or validate existing selection against current user's profile
  useEffect(() => {
    if (isBranchScoped) return; // branch-scoped users are driven by the lock effect below
    if (profileData && profileData.legalEntities.length > 0) {
      const isValid = allLegalEntities.some(le => le.id === legalEntityId);

      if (!legalEntityId || !isValid) {
        if (!isMasterUser) {
          const firstLe = profileData.legalEntities[0];
          setLegalEntity(firstLe.id, firstLe.name);
        }
      } else if (branchId && currentLe) {
        const isValidBranch = currentLe.branches.some(br => br.id === branchId);
        if (!isValidBranch) {
          setBranch(null, null);
        }
      }
    } else if (profileData && profileData.legalEntities.length === 0 && legalEntityId) {
      setLegalEntity(null, null);
    }
  }, [isBranchScoped, profileData, legalEntityId, branchId, setLegalEntity, setBranch, currentLe, allLegalEntities]);

  // Branch-scoped users: force the global context to their single branch, and to its legal
  // entity only while the legal-entity selector is genuinely locked. Forcing it
  // unconditionally snapped the selection straight back, so a user spanning several legal
  // entities could never leave the one their branch happens to sit under.
  useEffect(() => {
    if (!isBranchScoped) return;
    if (!profileData || profileData.legalEntities.length === 0) return;
    if (isLegalEntityLocked && lockedLe && legalEntityId !== lockedLe.id) {
      setLegalEntity(lockedLe.id, lockedLe.name);
    }
    if (lockedBranch && branchId !== lockedBranch.id) {
      setBranch(lockedBranch.id, lockedBranch.name);
    }
  }, [isBranchScoped, isLegalEntityLocked, profileData, lockedLe, lockedBranch, legalEntityId, branchId, setLegalEntity, setBranch]);

  // User menu opens on hover. Closing is delayed slightly so the pointer can cross the
  // gap between the avatar and the dropdown without the menu disappearing.
  const cancelMenuClose = () => {
    if (menuCloseTimer.current) {
      clearTimeout(menuCloseTimer.current);
      menuCloseTimer.current = null;
    }
  };
  const openUserMenu = () => {
    cancelMenuClose();
    setMenuOpen(true);
  };
  const scheduleUserMenuClose = () => {
    cancelMenuClose();
    menuCloseTimer.current = setTimeout(() => setMenuOpen(false), 150);
  };
  useEffect(() => cancelMenuClose, []);

  useEffect(() => {
    const closeAllMenus = () => {
      setMenuOpen(false);
      setOrgMenuOpen(false);
    };

    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current && !menuRef.current.contains(target)) setMenuOpen(false);
      if (orgRef.current && !orgRef.current.contains(target)) setOrgMenuOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeAllMenus();
    };
    // Clicks inside an iframe (e.g. Fusion V2 / Chatwoot) do not bubble to the
    // parent document, so mousedown outside-click handlers never run. Blur fires
    // when focus moves into the iframe, which closes open topbar menus.
    const handleWindowBlur = () => closeAllMenus();

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("blur", handleWindowBlur);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, []);

  return (
    <>
      <header className="h-full flex items-center justify-between gap-4 px-6">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          {/* Global Context Dropdowns */}
          <div className="flex items-center gap-3">
            {isMasterUser ? (
              <>
                {/* Organization (root Legal Entity) Dropdown — master users only */}
                <div className="relative group/dropdown" ref={orgRef}>
                  <button
                    type="button"
                    onClick={() => setOrgMenuOpen(!orgMenuOpen)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-bridge-border/60 transition-all active:scale-95 bg-bridge-surface hover:bg-bridge-panel"
                  >
                    <Building2 size={12} className="text-bridge-accent" />
                    <span className="text-[11px] font-bold text-bridge-fg uppercase tracking-tight truncate max-w-[120px]">
                      {currentOrg?.name || "Select Organization"}
                    </span>
                    <ChevronDown size={10} className={cn("text-bridge-muted transition-transform", orgMenuOpen && "rotate-180")} />
                  </button>

                  {orgMenuOpen && (
                    <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-premium border border-bridge-border overflow-hidden z-50 py-1.5 animate-in fade-in zoom-in duration-200">
                      <div className="px-3 py-1.5 mb-1 border-b border-bridge-border/50">
                        <span className="text-[10px] font-bold text-bridge-muted uppercase tracking-widest">Organizations</span>
                      </div>
                      <div className="px-2 pb-2">
                        <SearchInput
                          value={orgSearch}
                          onChange={setOrgSearch}
                          placeholder="Search organizations…"
                        />
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {filteredOrganizations.map((org) => (
                          <button
                            key={org.id}
                            onClick={() => {
                              // Selecting an organization scopes to the whole org (root LE).
                              setLegalEntity(org.id, org.name);
                              setBranch(null, null);
                              setOrgMenuOpen(false);
                            }}
                            className={cn(
                              "w-full flex items-center px-3 py-2 text-xs font-medium text-left transition-colors",
                              org.id === currentOrg?.id
                                ? "bg-bridge-accent/10 text-bridge-accent"
                                : "text-bridge-fg hover:bg-slate-50"
                            )}
                          >
                            {org.name}
                          </button>
                        ))}
                        {filteredOrganizations.length === 0 && (
                          <div className="px-3 py-2 text-[11px] text-bridge-muted italic">No organizations found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

              </>
            ) : (
              /* Everyone else belongs to exactly one organisation, so it is shown rather
                 than offered: without it the topbar gave no clue which account you were
                 working in once the entity and branch selectors moved onto the pages. */
              currentOrg && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-bridge-border/60 bg-bridge-surface">
                  <Building2 size={12} className="text-bridge-accent shrink-0" />
                  <span
                    className="text-[11px] font-bold text-bridge-fg uppercase tracking-tight truncate max-w-[220px]"
                    title={currentOrg.name}
                  >
                    {currentOrg.name}
                  </span>
                </div>
              )
            )}
            {/* The legal entity and branch selectors used to sit here. Scope is now picked
                on the page it applies to — see ScopePicker. */}
          </div>
        </div>

        {showProfileCompletionBanner && (
          <div className="flex-1 max-w-md mx-4">
            <div className="bg-bridge-accent/10 border border-bridge-accent/20 rounded-full px-4 py-1.5 flex items-center justify-between group cursor-pointer hover:bg-bridge-accent/20 transition-all" onClick={() => navigate("/profile")}>
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-bridge-accent animate-pulse" />
                <span className="text-xs font-semibold text-bridge-accent">Complete your profile to get started</span>
              </div>
              <span className="text-[10px] bg-bridge-accent text-white px-2 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">Setup Now</span>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          {/* <IconButton label="Notifications">
            <Bell size={16} />
          </IconButton> */}
          <ThemeSwitch />
          <div className="w-[1px] h-6 bg-bridge-border/60 mx-1" />
          <div
            className="relative"
            ref={menuRef}
            onMouseEnter={openUserMenu}
            onMouseLeave={scheduleUserMenuClose}
          >
            <button
              type="button"
              ref={menuTriggerRef}
              aria-label="Open user menu"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={openUserMenu}
              className="flex items-center gap-1 rounded-full focus-ring"
            >
              <Avatar
                name={topbarAgencyName}
                src={topbarLogoUrl}
                size="sm"
              />
              <ChevronDown
                size={12}
                className={cn("text-bridge-muted transition-transform duration-200", menuOpen && "rotate-180")}
              />
            </button>
            {menuMounted && (
              <div
                role="menu"
                onFocus={() => { focusInMenuRef.current = true; }}
                onBlur={(e) => {
                  // A null relatedTarget while the menu is still open is a genuine exit
                  // (e.g. click on non-focusable area); once closing, the inert-induced
                  // blur also has a null target and must keep the flag for focus restore.
                  const next = e.relatedTarget as Node | null;
                  if (next ? !e.currentTarget.contains(next) : menuOpen) {
                    focusInMenuRef.current = false;
                  }
                }}
                {...(menuOpen ? {} : { inert: "" })}
                aria-hidden={!menuOpen || undefined}
                className={cn(
                  "absolute right-0 mt-2 w-56 rounded-md border border-bridge-border bg-bridge-surface shadow-lg z-50 py-1 motion-reduce:animate-none",
                  menuOpen ? "animate-menuIn" : "animate-menuOut pointer-events-none",
                )}
              >
                <div className="px-3 py-2 border-b border-bridge-border/60">
                  <p className="text-sm font-semibold text-bridge-fg truncate">{displayName}</p>
                  {displayEmail && (
                    <p className="text-xs text-bridge-muted truncate">{displayEmail}</p>
                  )}
                </div>
                <UserMenuShortcutTooltip label="View Profile" shortcutKey="v">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate("/profile");
                    }}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors",
                      location.pathname === "/profile"
                        ? "text-[#009BF5] font-semibold"
                        : "text-bridge-fg hover:text-[#5CC0F8]"
                    )}
                  >
                    <User size={14} className="shrink-0" />
                    <span>View Profile</span>
                  </button>
                </UserMenuShortcutTooltip>
                <UserMenuShortcutTooltip label="Users" shortcutKey="u">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate("/users");
                    }}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors",
                      location.pathname === "/users"
                        ? "text-[#009BF5] font-semibold"
                        : "text-bridge-fg hover:text-[#5CC0F8]"
                    )}
                  >
                    <UserCog size={14} className="shrink-0" />
                    <span>Users</span>
                  </button>
                </UserMenuShortcutTooltip>
                <UserMenuShortcutTooltip label="Access Control" shortcutKey="a">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate("/access-control");
                    }}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors",
                      location.pathname.startsWith("/access-control")
                        ? "text-[#009BF5] font-semibold"
                        : "text-bridge-fg hover:text-[#5CC0F8]"
                    )}
                  >
                    <ShieldCheck size={14} className="shrink-0" />
                    <span>Access Control</span>
                  </button>
                </UserMenuShortcutTooltip>
                <UserMenuShortcutTooltip label="Settings" shortcutKey="s">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate("/settings");
                    }}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors",
                      location.pathname.startsWith("/settings")
                        ? "text-[#009BF5] font-semibold"
                        : "text-bridge-fg hover:text-[#5CC0F8]"
                    )}
                  >
                    <Settings size={14} className="shrink-0" />
                    <span>Settings</span>
                  </button>
                </UserMenuShortcutTooltip>
                <a
                  href="https://2f0til.share-eu1.hsforms.com/2qBtEtzJoQXKbzkLnuk0chQ"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setMenuOpen(false)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-bridge-fg hover:text-[#5CC0F8] transition-colors"
                >
                  <LifeBuoy size={14} className="shrink-0" />
                  <span>Raise a Support Ticket</span>
                </a>
                <div className="border-t border-bridge-border/60 mt-1 pt-1">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      logout();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-white/5 transition-colors"
                  >
                    <LogOut size={14} className="shrink-0" />
                    <span>Log out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
